// eslint-disable-next-line no-restricted-imports
import { useCallback, useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Flex,
  GridAlpha,
  Heading,
  Link,
  List,
  Text,
  useToasts,
} from '@applyboard/crystal-ui';
import { useEventTracker, useTranslation } from '@applyboard/ui-utils';
import AccountabilityImg from '../../assets/Accountability.png';
import ApplyBoardLogo from '../../assets/ApplyBoardLogo.png';
import DiversificationImg from '../../assets/Diversification.png';
import GicAndLoanBanner from '../../assets/GicAndLoanBanner.png';
import GicBanner from '../../assets/GicBanner.png';
import LikesImg from '../../assets/Likes.png';
import RewardsBannerAvatar from '../../assets/RewardsBannerAvatar.png';
import { RewardsLoanBanner } from '../../assets/RewardsLoanBanner';
import { useGetTrackingEventData } from '../../common/hooks/useQueryParams';
import { CreateRewardSignUpV1Request, RewardsError, useRewardSignUpV1 } from '../../data/rewards';
import FaqAccordion from './FaqAccordion';
export const COLUMN_SETTING = 'repeat(27, 1fr)';

const RewardsPage = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t } = useTranslation();
  const { rewardSignUp } = useRewardSignUpV1();
  const toasts = useToasts();
  const { rpId, source } = useGetTrackingEventData();
  const { trackEvent } = useEventTracker();
  const [isCTADisable, setIsCTADisable] = useState(false);

  useEffect(() => {
    trackEvent({
      event: 'VAS - Rewards Landing Page - Page View',
      data: { rp_id: rpId, source: source },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitCallback = useCallback(
    async (formValues: CreateRewardSignUpV1Request) => {
      rewardSignUp(
        {
          rp_id: formValues.rp_id,
          source: formValues.source,
        },
        () => {
          // We are keeping the event name the same for now (for both waitlist and Program signup)
          trackEvent({
            event: 'VAS - Rewards Landing Page - Sign Up: Completed',
            data: { rp_id: rpId, source: source },
          });
          toasts.positive(
            `Thanks for your interest, we will let you know when the program is available to you!`,
          );
          // toasts.positive(`Congratulations! You've been enrolled in ApplyBoard Rewards!`);
        },
        error => {
          console.error('Error signing up for rewards:', error);
          if (error instanceof RewardsError) {
            toasts.negative(new Error(error.message));
            const rewardsError = error as RewardsError;
            const errorMessage =
              rewardsError.errorBody.message instanceof Array
                ? rewardsError.errorBody.message.join(',')
                : rewardsError.errorBody.message;
            trackEvent({
              event: 'VAS - Rewards Landing Page - Sign Up: Errored',
              data: { rp_id: rpId, source: source },
              error: new Error(errorMessage),
            });
          } else {
            toasts.negative(new Error('An unexpected error occurred'));
            trackEvent({
              event: 'VAS - Rewards Landing Page - Errored',
              data: { rp_id: rpId, source: source },
              error: new Error('An unexpected error occurred'),
            });
          }
        },
      );
    },
    [rewardSignUp, rpId, source, toasts, trackEvent],
  );

  return rpId ? (
    <div>
      <GridAlpha columns={COLUMN_SETTING} rowGap={6}>
        <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={{ xs: 27, md: 26 }}>
          <img src={ApplyBoardLogo} alt="ApplyBoard Logo" style={{ display: 'block' }} />
        </GridAlpha.Item>
      </GridAlpha>

      {/* Marketing Heading Banner */}
      <Box gradient="primary" gradientDirection="horizontal-reverse">
        <GridAlpha columns={COLUMN_SETTING} rowGap={6}>
          <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={{ xs: 27, md: 18 }}>
            <Flex
              direction="column"
              gap={6}
              justify="center"
              // pl={16}
              pt={{ xs: 12, md: 32 }}
              grow={1}
            >
              <Text as="p" variant="headlineS" contrast="inverted">
                ApplyBoard Rewards
              </Text>
              <Text as="p" variant="headlineL" contrast="inverted">
                Maximize Your Earnings
              </Text>
              <Text as="p" variant="bodyL" contrast="inverted">
                ApplyBoard Rewards is an exclusive and limited-time program that will increase your
                rewards when your student uses multiple services offered by us.
              </Text>
            </Flex>
          </GridAlpha.Item>

          <GridAlpha.Item columnStart={{ xs: 2, md: 21 }} columnEnd={{ xs: 27, md: 26 }}>
            <Flex justify="center" align="end" pt={{ sm: 4, md: 16 }}>
              <img
                src={RewardsBannerAvatar}
                alt="Rewards Banner Avatar"
                height={400}
                width="auto"
              />
            </Flex>
          </GridAlpha.Item>
        </GridAlpha>
      </Box>

      <Box pt={12}>
        <Flex align="center" direction="column" gap={4} grow={1} justify="center">
          <Heading level={2} align="start" variant={{ sm: 'headlineS', md: 'headlineM' }}>
            Current Offers
          </Heading>
          <Box pb={4}>
            <Text align="center" variant="bodyL">
              Earn extra, in addition to your standard commissions when the same student
              participates
            </Text>
            <Text align="center" variant="bodyL">
              in multiple services from ApplyBoard.
            </Text>
          </Box>
          <Button
            width="hug"
            emphasis="contained"
            intent="primary"
            onClick={() => {
              setIsCTADisable(true);
              submitCallback({ rp_id: rpId, source: source });
            }}
            disabled={isCTADisable}
          >
            Join the Waitlist
            {/* Join the Program */}
          </Button>
          <Text align="center" variant="bodyS">
            By joining the waitlist, you are agreeing to our{' '}
            {/* By joining the Program, you are agreeing to our{' '} */}
            <Link
              aria-label=""
              href="https://www.applyboard.com/info/rewards-terms-and-conditions"
              onClick={() => {
                trackEvent({
                  event: 'VAS - Rewards Landing Page - Terms and Conditions Link Clicked',
                  data: { rp_id: rpId, source: source },
                });
              }}
              mode="override_user_choice_and_open_in_new_tab"
              saliency="default"
            >
              Rewards Terms and Conditions
            </Link>
          </Text>
        </Flex>
      </Box>
      {/* Offer Section */}
      <GridAlpha columns={COLUMN_SETTING} rowGap={4} pt={{ xs: 6, md: 12 }} pb={12}>
        <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={{ xs: 27, md: 10 }}>
          <Card height="fill">
            <img
              src={GicBanner}
              alt="GicBanner"
              style={{
                height: '80px',
                width: 'auto',
                objectFit: 'contain',
              }}
            />
            <Flex direction="column" align="start" gap={6} grow={1} pt={6}>
              <Text variant="headlineS" intent="primary" align="center">
                Extra $100 CAD
              </Text>
              <Text align="start">
                <strong>What do you have to do?</strong>
              </Text>
              <List aria-label="List of offers">
                <List.Item>Enroll a student through ApplyBoard</List.Item>
                <List.Item>Secure his/her GIC via ApplyBoard</List.Item>
              </List>
            </Flex>
            <Flex direction="column" justify="end" align="start" grow={0}>
              <Text align="start" contrast="low" intent="secondary">
                *This offer is limited to 5 students during the duration of this program.
              </Text>
            </Flex>
          </Card>
        </GridAlpha.Item>
        <GridAlpha.Item columnStart={{ xs: 2, md: 11 }} columnEnd={{ xs: 27, md: 18 }}>
          <Card height="fill">
            <Flex direction="column" align="center">
              <RewardsLoanBanner />
            </Flex>
            <Flex direction="column" align="start" gap={6} grow={1} pt={6}>
              <Text variant="headlineS" intent="primary" align="center">
                Extra $100 CAD
              </Text>
              <Text align="start">
                <strong>What do you have to do?</strong>
              </Text>
              <List aria-label="List of offers">
                <List.Item>Enroll a student through ApplyBoard</List.Item>
                <List.Item>Secure his/her student loan through ApplyBoard.</List.Item>
              </List>
            </Flex>
            <Flex direction="column" justify="end" align="start" grow={0}>
              <Text align="start" contrast="low" intent="secondary">
                *This offer is limited to 5 students during the duration of this program.
              </Text>
            </Flex>
          </Card>
        </GridAlpha.Item>
        <GridAlpha.Item columnStart={{ xs: 2, md: 19 }} columnEnd={{ xs: 27, md: 26 }}>
          <Card height="fill">
            <img
              src={GicAndLoanBanner}
              alt="GicAndLoanBanner"
              style={{ height: '80px', width: 'auto', objectFit: 'contain' }}
            />
            <Flex direction="column" align="start" gap={6} grow={1} pt={6}>
              <Text variant="headlineS" intent="primary" align="center">
                Extra $200 CAD
              </Text>
              <Text align="start">
                <strong>What do you have to do?</strong>
              </Text>
              <List aria-label="List of offers">
                <List.Item>Enroll a student through ApplyBoard</List.Item>
                <List.Item>Secure his/her GIC via ApplyBoard</List.Item>
                <List.Item>Secure his/her student Loan via ApplyBoard</List.Item>
              </List>
            </Flex>
            <Flex direction="column" justify="end" align="start" grow={0} pt={4}>
              <Text align="start" contrast="low">
                *This offer is limited to 5 students during the duration of this program.
              </Text>
            </Flex>
          </Card>
        </GridAlpha.Item>
      </GridAlpha>

      {/* About */}
      <div
        style={{
          backgroundColor: '#FFFFFF',
          width: '100%',
          display: 'flex',
          justifyContent: 'start',
          alignItems: 'center',
        }}
      >
        <GridAlpha columns={COLUMN_SETTING} rowGap={4} pb={12} pt={12}>
          <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={{ xs: 27, md: 26 }}>
            <Text contrast="mid" intent="secondary" variant="bodyL" align="center">
              ABOUT
            </Text>
          </GridAlpha.Item>
          <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={{ xs: 27, md: 26 }}>
            <Heading level={1} align="center" variant={{ sm: 'headlineS', md: 'headlineM' }}>
              More for you and for your students
            </Heading>
          </GridAlpha.Item>
          <GridAlpha.Item columnStart={1} columnEnd={28}>
            <GridAlpha columns={COLUMN_SETTING} rowGap={6} pb={6} pt={6}>
              <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={{ xs: 27, md: 10 }}>
                <Card height="fill">
                  <Flex align="center" justify="center" direction="column" gap={6} grow={1}>
                    <img
                      src={DiversificationImg}
                      alt="Diversification"
                      style={{ width: '120px', height: '120px' }}
                    />

                    <Text intent="primary" variant="titleL" align="center">
                      Maximize Your Earnings
                    </Text>
                  </Flex>
                  <Flex align="end" justify="center" direction="column" grow={0} pt={6}>
                    <Text align="center" contrast="low" intent="secondary">
                      Boost average revenue per student by offering rewards for using multiple
                      ApplyBoard services.
                    </Text>
                  </Flex>
                </Card>
              </GridAlpha.Item>

              <GridAlpha.Item columnStart={{ xs: 2, md: 11 }} columnEnd={{ xs: 27, md: 18 }}>
                <Card height="fill">
                  <Flex align="center" justify="center" direction="column" gap={6} grow={1}>
                    <img
                      src={AccountabilityImg}
                      alt="Accountability"
                      style={{ width: '120px', height: '120px' }}
                    />
                    <Text intent="primary" variant="titleL" align="center">
                      Strengthen Student Relationships
                    </Text>
                  </Flex>
                  <Flex align="end" justify="center" direction="column" grow={0} pt={6}>
                    <Text align="center" contrast="low" intent="secondary">
                      Become a trusted advisor by meeting more of your students&apos; needs.
                    </Text>
                  </Flex>
                </Card>
              </GridAlpha.Item>

              <GridAlpha.Item columnStart={{ xs: 2, md: 19 }} columnEnd={{ xs: 27, md: 26 }}>
                <Card height="fill">
                  <Flex align="center" justify="center" direction="column" gap={6} grow={1}>
                    <img src={LikesImg} alt="Likes" style={{ width: '120px', height: '120px' }} />
                    <Text intent="primary" variant="titleL" align="center">
                      Stay ahead in the market
                    </Text>
                  </Flex>
                  <Flex align="end" justify="center" direction="column" grow={0} pt={6}>
                    <Text align="center" contrast="low" intent="secondary">
                      Offer a full suite of products and expand your business with new revenue
                      streams.
                    </Text>
                  </Flex>
                </Card>
              </GridAlpha.Item>
            </GridAlpha>
          </GridAlpha.Item>
        </GridAlpha>
      </div>

      <GridAlpha columns={COLUMN_SETTING} rowGap={6} pb={12} pt={12}>
        <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={{ xs: 27, md: 26 }}>
          <Text contrast="mid" intent="secondary" variant="bodyL" align="center">
            FAQs
          </Text>
        </GridAlpha.Item>
        <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={{ xs: 27, md: 26 }}>
          <Heading level={1} align="center" variant={{ sm: 'headlineS', md: 'headlineM' }}>
            Frequently Asked Questions
          </Heading>
        </GridAlpha.Item>
        <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={{ xs: 27, md: 26 }}>
          <FaqAccordion />
        </GridAlpha.Item>
      </GridAlpha>
    </div>
  ) : (
    <Redirect to="/not_found" />
  );
};

export default RewardsPage;
